import { createTheme } from "@mui/material/styles";
import {color, font, radius, shadow, type} from "common/tokens";

// Scoped theme for the date and time fields. MUI pickers render their calendar
// and clock through a portal, so form-level CSS cannot reach them — the styling
// has to come down as theme overrides instead.
//
// Palette is deliberately left alone: MUI derives light/dark variants from it
// and cannot parse the `var(--…)` values the tokens resolve to.
const pickerTheme = createTheme({
  typography: {
    fontFamily: font.SANS,
  },
  components: {
    MuiButtonBase: {
      defaultProps: {disableRipple: true},
    },
    MuiPickersPopper: {
      // The popper traps focus by default, which pulls the caret out of the
      // field the moment it opens — the text could no longer be edited.
      defaultProps: {
        TrapFocusProps: {disableEnforceFocus: true},
      },
      styleOverrides: {
        paper: {
          borderRadius: 14,
          border: `1px solid ${color.CARD_BORDER}`,
          boxShadow: shadow.POPOVER,
          marginTop: 6,
        },
      },
    },
    MuiCalendarPicker: {
      styleOverrides: {
        root: {
          minWidth: 296,
          minHeight: 'auto',
          padding: '6px 8px 10px',
          '& .PrivatePickersYear-yearButton.Mui-selected': {
            color: color.ACCENT_TEXT,
            fontWeight: font.BOLD,
          },
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {
          margin: '4px 0 8px',
        },
        label: {
          ...type.meta,
          fontSize: 14,
          color: color.TEXT,
        },
        switchViewButton: {
          padding: 6,
          color: color.TEXT_MUTED,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: color.SURFACE_MUTED,
          },
        },
      },
    },
    MuiDayPicker: {
      styleOverrides: {
        weekDayLabel: {
          ...type.label,
          color: color.TEXT_SUBTLE,
          width: 36,
        },
        slideTransition: {
          marginTop: 2,
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          ...type.body,
          width: 36,
          height: 36,
          margin: '1px 0',
          borderRadius: radius.CONTROL,
          color: color.TEXT,
          '&:hover': {
            backgroundColor: color.SURFACE_MUTED,
          },
          '&.Mui-selected': {
            backgroundColor: color.ACCENT,
            color: color.ON_ACCENT,
            fontWeight: font.BOLD,
            '&:hover, &:focus': {
              backgroundColor: color.ACCENT,
            },
          },
          '&.Mui-disabled': {
            color: color.TEXT_SUBTLE,
          },
        },
        today: {
          color: color.ACCENT_TEXT,
          fontWeight: font.BOLD,
        },
      },
    },
    MuiPickersToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: color.ACCENT_TINT,
          height: 'auto',
          padding: '10px 20px',
          color: color.TEXT_MUTED,
          '& .Mui-selected': {
            color: color.TEXT,
          },
        },
        // The field behind the picker is already typable, so the toggle into
        // keyboard entry is a second door onto the same room.
        penIconButton: {
          display: 'none',
        },
      },
    },
    MuiClock: {
      styleOverrides: {
        clock: {
          backgroundColor: color.SURFACE_MUTED,
        },
        // The desktop clock plants AM and PM in its bottom corners whatever
        // ampmInClock says, and the toolbar above already carries both.
        amButton: {
          display: 'none',
        },
        pmButton: {
          display: 'none',
        },
        pin: {
          backgroundColor: color.ACCENT,
        },
      },
    },
    MuiClockNumber: {
      styleOverrides: {
        root: {
          ...type.body,
          color: color.TEXT,
          '&.Mui-selected': {
            color: color.ON_ACCENT,
          },
        },
      },
    },
    MuiClockPointer: {
      styleOverrides: {
        root: {
          backgroundColor: color.ACCENT,
        },
        thumb: {
          backgroundColor: color.ACCENT,
          borderColor: color.ACCENT,
        },
      },
    },
  },
});

// Anchors the calendar under the field and leaves focus in the input, so the
// value can still be typed while the popover is open.
export const pickerPopperProps = {
  placement: 'bottom-start',
};

export default pickerTheme;
